/**
 * Product Lumen Section Scripts
 * @fileoverview product.lumenページ専用のスクリプト
 */

/**
 * Trustpilotモーダルコンポーネント
 * @class
 */
class ProductLumenHeroTrustpilotModal {
  /**
   * @param {HTMLElement} sectionElement - ヒーローセクション要素
   */
  constructor(sectionElement) {
    this.section = sectionElement;
    this.button = this.section.querySelector('.product-lumen-hero-section__trustpilot-button');
    this.isOpen = false;
    this.init();
  }

  /**
   * 初期化
   * @private
   */
  init() {
    if (!this.button) return;
    this.button.addEventListener('click', () => this.open());
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });
  }

  /**
   * モーダルを開く
   */
  open() {
    if (window.Trustpilot) {
      this.isOpen = true;
      this.updateUI();
    } else {
      window.open('https://www.trustpilot.com/review/www.lumen.me', '_blank');
    }
  }

  /**
   * モーダルを閉じる
   */
  close() {
    this.isOpen = false;
    this.updateUI();
  }

  /**
   * UI状態を更新
   * @private
   */
  updateUI() {
    this.section.classList.toggle('product-lumen-hero-section--modal-open', this.isOpen);
    this.button.setAttribute('aria-expanded', String(this.isOpen));
    document.body.style.overflowY = this.isOpen ? 'hidden' : 'auto';
  }
}

/**
 * ヒーローアコーディオンコンポーネント
 * @class
 */
class ProductLumenHeroAccordion {
  /**
   * @param {HTMLElement} accordionElement - アコーディオン要素
   */
  constructor(accordionElement) {
    this.accordion = accordionElement;
    this.items = this.accordion.querySelectorAll('.product-lumen-hero-section__accordion-item');
    this.openIndex = null;
    this.init();
  }

  /**
   * 初期化
   * @private
   */
  init() {
    this.items.forEach((item, index) => {
      const trigger = item.querySelector('.product-lumen-hero-section__accordion-trigger');
      const content = item.querySelector('.product-lumen-hero-section__accordion-content');
      const icon = item.querySelector('.product-lumen-hero-section__accordion-icon');

      if (trigger && content) {
        trigger.addEventListener('click', () => this.toggle(index));

        const isInitiallyOpen = trigger.getAttribute('aria-expanded') === 'true';
        if (isInitiallyOpen) {
          this.openIndex = index;
          content.classList.add('product-lumen-hero-section__accordion-content--open');
          content.style.maxHeight = content.scrollHeight + 'px';
          if (icon) icon.classList.add('product-lumen-hero-section__accordion-icon--open');
        } else {
          trigger.setAttribute('aria-expanded', 'false');
        }
      }
    });
  }

  /**
   * アコーディオンを切り替え
   * @param {number} index - アイテムのインデックス
   */
  toggle(index) {
    if (this.openIndex === index) {
      this.close(index);
      this.openIndex = null;
    } else {
      if (this.openIndex !== null) this.close(this.openIndex);
      this.open(index);
      this.openIndex = index;
    }
  }

  /**
   * アコーディオンを開く
   * @param {number} index - アイテムのインデックス
   */
  open(index) {
    const item = this.items[index];
    const trigger = item.querySelector('.product-lumen-hero-section__accordion-trigger');
    const content = item.querySelector('.product-lumen-hero-section__accordion-content');
    const icon = item.querySelector('.product-lumen-hero-section__accordion-icon');

    content.classList.add('product-lumen-hero-section__accordion-content--open');
    content.style.maxHeight = content.scrollHeight + 'px';
    trigger.setAttribute('aria-expanded', 'true');
    if (icon) icon.classList.add('product-lumen-hero-section__accordion-icon--open');
  }

  /**
   * アコーディオンを閉じる
   * @param {number} index - アイテムのインデックス
   */
  close(index) {
    const item = this.items[index];
    const trigger = item.querySelector('.product-lumen-hero-section__accordion-trigger');
    const content = item.querySelector('.product-lumen-hero-section__accordion-content');
    const icon = item.querySelector('.product-lumen-hero-section__accordion-icon');

    content.style.maxHeight = '0px';
    content.classList.remove('product-lumen-hero-section__accordion-content--open');
    trigger.setAttribute('aria-expanded', 'false');
    if (icon) icon.classList.remove('product-lumen-hero-section__accordion-icon--open');
  }
}

/**
 * ヒーローメディアスライダーコンポーネント
 * @class
 */
class ProductLumenHeroMediaSlider {
  /**
   * @param {HTMLElement} sliderElement - スライダー要素
   */
  constructor(sliderElement) {
    this.slider = sliderElement;
    this.swiper = null;
    this.videos = [];
    this.currentVideo = null;
    this.init();
  }

  /**
   * 初期化
   * @private
   */
  init() {
    this.swiper = new Swiper(this.slider, {
      slidesPerView: 1,
      spaceBetween: 0,
      loop: false,
      breakpoints: {
        0: {
          slidesPerView: 1.2,
          spaceBetween: 16,
          slidesOffsetBefore: 24,
          slidesOffsetAfter: 24,
        },
        768: {
          slidesPerView: 1.5,
          spaceBetween: 24,
          slidesOffsetBefore: 36,
          slidesOffsetAfter: 36,
        },
        1024: {
          slidesPerView: 1,
          spaceBetween: 0,
          slidesOffsetBefore: 0,
          slidesOffsetAfter: 0,
        },
      },
      navigation: {
        nextEl: '.product-lumen-hero-section__media-nav-button--next',
        prevEl: '.product-lumen-hero-section__media-nav-button--prev',
      },
      pagination: {
        el: '.product-lumen-hero-section__media-pagination',
        clickable: true,
      },
      on: {
        slideChangeTransitionEnd: () => this.handleSlideChange(),
      },
    });

    this.videos = [...this.slider.querySelectorAll('[data-video]')];
    this.setupMuteToggles();
    requestAnimationFrame(() => this.handleSlideChange());
  }

  /**
   * ミュートトグルをセットアップ
   * @private
   */
  setupMuteToggles() {
    const muteToggles = this.slider.querySelectorAll('[data-mute-toggle]');
    muteToggles.forEach((toggle) => {
      const slide = toggle.closest('.swiper-slide');
      const video = slide?.querySelector('[data-video]');
      if (!video) return;

      video.muted = true;
      toggle.setAttribute('data-muted', 'true');
      toggle.addEventListener('click', () => {
        video.muted = !video.muted;
        toggle.setAttribute('data-muted', String(video.muted));
      });
    });
  }

  /**
   * スライド変更時の処理
   * @private
   */
  handleSlideChange() {
    const activeSlide = this.slider.querySelector('.swiper-slide-active');
    if (!activeSlide) return;

    const activeVideo = activeSlide.querySelector('[data-video]');
    this.videos.forEach((video) => {
      if (!video.paused) video.pause();
    });

    if (!activeVideo) {
      this.currentVideo = null;
      return;
    }

    this.currentVideo = activeVideo;
    this.safePlay(activeVideo);
  }

  /**
   * 安全にビデオを再生
   * @param {HTMLVideoElement} video - ビデオ要素
   * @private
   */
  safePlay(video) {
    requestAnimationFrame(() => {
      // iOS Safari/Chrome 対策: inline + muted での再生を強制
      try {
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
      } catch (_) {
        // noop
      }

      const promise = video.play();
      if (promise?.then) {
        promise.catch(() => {
          requestAnimationFrame(() => video.play().catch(() => {}));
        });
      }
    });
  }

  /**
   * 破棄
   */
  destroy() {
    if (this.swiper) this.swiper.destroy(true, true);
  }
}

/**
 * 可視性監視コンポーネント（Intersection Observer）
 * @class
 */
class ProductLumenHeroVisibilityObserver {
  /**
   * @param {HTMLElement} element - 監視対象要素
   * @param {Function} callback - 可視状態変更時のコールバック
   */
  constructor(element, callback) {
    this.element = element;
    this.callback = callback;
    this.observer = null;
    this.init();
  }

  /**
   * 初期化
   * @private
   */
  init() {
    if (!this.element || !('IntersectionObserver' in window)) return;

    this.observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => this.callback(entry.isIntersecting)),
      { threshold: 0, rootMargin: '0px' },
    );
    this.observer.observe(this.element);
  }

  /**
   * 監視を停止
   */
  disconnect() {
    this.observer?.disconnect();
  }
}

/**
 * Habitsスライダーコンポーネント
 * @class
 */
class HabitsSlider {
  /**
   * @param {HTMLElement} sectionElement - セクション要素
   * @param {Object} options - オプション
   * @param {number} [options.speed=1000] - アニメーション速度
   * @param {number} [options.slidesPerGroup=1] - グループあたりのスライド数
   * @param {number} [options.spaceBetween=24] - スライド間のスペース
   * @param {Object} [options.breakpoints] - ブレークポイント設定
   */
  constructor(sectionElement, options = {}) {
    this.section = sectionElement;
    this.options = {
      speed: options.speed || 1000,
      slidesPerGroup: options.slidesPerGroup || 1,
      spaceBetween: options.spaceBetween || 24,
      breakpoints: options.breakpoints || {
        0: { slideWidth: 323 },
        768: { slideWidth: 522 },
        1920: { slideWidth: 692 },
      },
    };
    this.swiper = null;
    this.resizeObserver = null;
    this.init();
  }

  /**
   * 初期化
   * @private
   */
  init() {
    if (!this.section) return;
    const container = this.section.querySelector('.swiper');
    if (!container) return;

    const containerLg = this.section.querySelector('[data-container="lg"]');

    this.swiper = new Swiper(container, {
      centeredSlides: false,
      loop: false,
      rewind: true,
      autoplay: {
        delay: 3000,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      },
      slidesPerGroup: this.options.slidesPerGroup,
      slidesPerView: this.calculateSlidesPerView(),
      spaceBetween: this.options.spaceBetween,
      speed: this.options.speed,
      slidesOffsetBefore: this.calculateSlidesOffsetBefore(containerLg),
      slidesOffsetAfter: 24,
      breakpoints: {
        0: {
          slidesPerView: 1.2,
          slidesOffsetAfter: 24,
          spaceBetween: 16,
        },
        768: {
          slidesPerView: this.calculateSlidesPerView(),
          slidesOffsetAfter: 36,
          spaceBetween: this.options.spaceBetween,
        },
      },
      on: {
        init: () => this.updateSlidesPerView(),
        resize: () => this.updateSlidesPerView(),
      },
      navigation: {
        nextEl: '.product-lumen-habits-slider__navigation .swiper-button-next',
        prevEl: '.product-lumen-habits-slider__navigation .swiper-button-prev',
      },
    });

    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (this.swiper) {
          this.swiper.params.slidesOffsetBefore = this.calculateSlidesOffsetBefore(containerLg);
          this.swiper.update();
        }
      }, 100);
    });

    this.resizeObserver = new ResizeObserver(() => this.updateSlidesPerView());
    this.resizeObserver.observe(this.section);
  }

  /**
   * スライドの左オフセットを計算
   * @param {HTMLElement} containerLg - data-container="lg"要素
   * @returns {number} 左オフセット値
   * @private
   */
  calculateSlidesOffsetBefore(containerLg) {
    if (containerLg) {
      const containerRect = containerLg.getBoundingClientRect();
      const computedStyle = window.getComputedStyle(containerLg);
      const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;

      return containerRect.left + paddingLeft;
    }
    return 0;
  }

  /**
   * 表示スライド数を計算
   * @returns {number} 表示スライド数
   * @private
   */
  calculateSlidesPerView() {
    const windowWidth = window.innerWidth;
    const containerWidth = this.section.offsetWidth;
    const { spaceBetween, breakpoints } = this.options;

    let slideWidth;
    if (windowWidth < 768) {
      slideWidth = breakpoints[0].slideWidth;
    } else if (windowWidth < 1920) {
      slideWidth = breakpoints[768].slideWidth;
    } else {
      slideWidth = breakpoints[1920].slideWidth;
    }

    return Math.floor((containerWidth / (slideWidth + spaceBetween)) * 100) / 100;
  }

  /**
   * 表示スライド数を更新
   * @private
   */
  updateSlidesPerView() {
    if (!this.swiper?.params) return;
    const windowWidth = window.innerWidth;
    this.swiper.params.slidesPerView = windowWidth < 768 ? 1.2 : this.calculateSlidesPerView();
    this.swiper.update();
  }

  /**
   * 破棄
   */
  destroy() {
    this.resizeObserver?.disconnect();
    this.swiper?.destroy(true, true);
    this.resizeObserver = null;
    this.swiper = null;
  }
}

/**
 * ハードウェア特性コンポーネント
 * @class
 */
class HardwareCharacteristics {
  /**
   * @param {HTMLElement} sectionElement - セクション要素
   */
  constructor(sectionElement) {
    this.section = sectionElement;
    this.videos = [];
    this.isPlaying = true;
    this.playButton = null;
    this.init();
  }

  /**
   * 初期化
   * @private
   */
  init() {
    if (!this.section) return;
    this.initVideoControls();
    this.initAccordion();
  }

  /**
   * ビデオコントロールを初期化
   * @private
   */
  initVideoControls() {
    this.videos = Array.from(this.section.querySelectorAll('video'));
    this.playButton = this.section.querySelector(
      '.product-lumen-hardware-characteristics__play-button',
    );
    if (!this.playButton || this.videos.length === 0) return;

    this.playButton.addEventListener('click', () => this.togglePlayback());
    this.updatePlayButtonState();
  }

  /**
   * 再生/一時停止を切り替え
   * @private
   */
  togglePlayback() {
    this.isPlaying = !this.isPlaying;
    this.videos.forEach((video) => (this.isPlaying ? video.play() : video.pause()));
    this.updatePlayButtonState();
  }

  /**
   * 再生ボタンの状態を更新
   * @private
   */
  updatePlayButtonState() {
    if (!this.playButton) return;
    const playIcon = this.playButton.querySelector(
      '.product-lumen-hardware-characteristics__icon--play',
    );
    const pauseIcon = this.playButton.querySelector(
      '.product-lumen-hardware-characteristics__icon--pause',
    );

    if (playIcon && pauseIcon) {
      playIcon.style.display = this.isPlaying ? 'none' : 'block';
      pauseIcon.style.display = this.isPlaying ? 'block' : 'none';
    }
  }

  /**
   * アコーディオンを初期化
   * @private
   */
  initAccordion() {
    const items = this.section.querySelectorAll('.product-lumen-hardware-characteristics__item');
    if (items.length === 0) return;

    items.forEach((item) => {
      const trigger = item.querySelector('.product-lumen-hardware-characteristics__trigger');
      const content = item.querySelector('.product-lumen-hardware-characteristics__content');
      const icon = item.querySelector('.product-lumen-hardware-characteristics__icon');
      if (!trigger || !content) return;

      const isInitiallyOpen = trigger.getAttribute('aria-expanded') === 'true';

      if (isInitiallyOpen) {
        content.classList.add('is-open');
        icon?.classList.add('product-lumen-hardware-characteristics__icon--open');
      } else {
        content.classList.remove('is-open');
        trigger.setAttribute('aria-expanded', 'false');
        icon?.classList.remove('product-lumen-hardware-characteristics__icon--open');
      }

      trigger.addEventListener('click', () => {
        const isOpen = content.classList.contains('is-open');

        if (isOpen) {
          content.classList.remove('is-open');
          trigger.setAttribute('aria-expanded', 'false');
          icon?.classList.remove('product-lumen-hardware-characteristics__icon--open');
        } else {
          content.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
          icon?.classList.add('product-lumen-hardware-characteristics__icon--open');
          setTimeout(() => item.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
        }
      });
    });
  }

  /**
   * 破棄
   */
  destroy() {
    this.videos = [];
    this.playButton = null;
  }
}

/**
 * ユーザーエクスペリエンススライダーコンポーネント
 * @class
 */
class UserExperienceSlider {
  /**
   * @param {HTMLElement} sectionElement - セクション要素
   * @param {Object} options - オプション
   */
  constructor(sectionElement, options = {}) {
    this.section = sectionElement;
    this.options = {
      speed: options.speed || 1000,
      slidesPerGroup: options.slidesPerGroup || 1,
      spaceBetween: options.spaceBetween || 16,
      breakpoints: options.breakpoints || {
        0: { slideWidth: 323 },
        768: { slideWidth: 410 },
        1200: { slideWidth: 520 },
      },
    };
    this.swiper = null;
    this.resizeObserver = null;
    this.init();
  }

  /**
   * 初期化
   * @private
   */
  init() {
    if (!this.section) return;
    const container = this.section.querySelector('.swiper');
    if (!container) return;

    const containerLg = this.section.querySelector('[data-container="lg"]');

    this.swiper = new Swiper(container, {
      loop: false,
      rewind: true,
      autoplay: {
        delay: 3000,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      },
      slidesPerGroup: this.options.slidesPerGroup,
      slidesPerView: this.calculateSlidesPerView(),
      spaceBetween: this.options.spaceBetween,
      speed: this.options.speed,
      slidesOffsetBefore: this.calculateSlidesOffsetBefore(containerLg),
      slidesOffsetAfter: 24,
      breakpoints: {
        0: {
          slidesPerView: 1.2,
          slidesOffsetAfter: 24,
          spaceBetween: 16,
        },
        768: {
          slidesPerView: 2.3,
          slidesOffsetAfter: 36,
          spaceBetween: this.options.spaceBetween,
        },
        1024: {
          slidesPerView: 4.2,
          slidesOffsetAfter: 36,
          spaceBetween: this.options.spaceBetween,
        },
      },
      on: {
        init: () => this.updateSlidesPerView(),
        resize: () => this.updateSlidesPerView(),
      },
      navigation: {
        nextEl: '.product-lumen-user-experience__navigation .swiper-button-next',
        prevEl: '.product-lumen-user-experience__navigation .swiper-button-prev',
      },
    });

    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (this.swiper) {
          this.swiper.params.slidesOffsetBefore = this.calculateSlidesOffsetBefore(containerLg);
          this.swiper.update();
        }
      }, 100);
    });

    this.resizeObserver = new ResizeObserver(() => this.updateSlidesPerView());
    this.resizeObserver.observe(this.section);
  }

  /**
   * スライドの左オフセットを計算
   * @param {HTMLElement} containerLg - data-container="lg"要素
   * @returns {number} 左オフセット値
   * @private
   */
  calculateSlidesOffsetBefore(containerLg) {
    if (containerLg) {
      const containerRect = containerLg.getBoundingClientRect();
      const computedStyle = window.getComputedStyle(containerLg);
      const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;

      return containerRect.left + paddingLeft;
    }
    return 0;
  }

  /**
   * 表示スライド数を計算
   * @returns {number}
   * @private
   */
  calculateSlidesPerView() {
    const windowWidth = window.innerWidth;
    const containerWidth = this.section.offsetWidth;
    const { spaceBetween, breakpoints } = this.options;

    let slideWidth;
    if (windowWidth < 768) {
      slideWidth = breakpoints[0].slideWidth;
    } else if (windowWidth < 1200) {
      slideWidth = breakpoints[768].slideWidth;
    } else {
      slideWidth = breakpoints[1200].slideWidth;
    }

    return Math.floor((containerWidth / (slideWidth + spaceBetween)) * 100) / 100;
  }

  /**
   * 表示スライド数を更新
   * @private
   */
  updateSlidesPerView() {
    if (!this.swiper?.params) return;
    const windowWidth = window.innerWidth;
    if (windowWidth < 768) {
      this.swiper.params.slidesPerView = 1.2;
    } else if (windowWidth < 1024) {
      this.swiper.params.slidesPerView = 2.3;
    } else {
      this.swiper.params.slidesPerView = 4.2;
    }
    this.swiper.update();
  }

  /**
   * 破棄
   */
  destroy() {
    this.resizeObserver?.disconnect();
    this.swiper?.destroy(true, true);
    this.resizeObserver = null;
    this.swiper = null;
  }
}

/**
 * ユーザーレビュースライダーコンポーネント
 * @class
 */
class UserReviewSlider {
  /**
   * @param {HTMLElement} sectionElement - セクション要素
   * @param {Object} options - オプション
   */
  constructor(sectionElement, options = {}) {
    this.section = sectionElement;
    this.options = {
      speed: options.speed || 1000,
      slidesPerGroup: options.slidesPerGroup || 1,
      spaceBetween: options.spaceBetween || 16,
      breakpoints: options.breakpoints || {
        0: { slideWidth: 323 },
        768: { slideWidth: 410 },
        1200: { slideWidth: 520 },
      },
    };
    this.swiper = null;
    this.resizeObserver = null;
    this.init();
  }

  /**
   * 初期化
   * @private
   */
  init() {
    if (!this.section) return;
    const container = this.section.querySelector('.swiper');
    if (!container) return;

    const containerLg = this.section.querySelector('[data-container="lg"]');

    this.swiper = new Swiper(container, {
      loop: false,
      rewind: true,
      autoplay: {
        delay: 3000,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      },
      slidesPerGroup: this.options.slidesPerGroup,
      slidesPerView: this.calculateSlidesPerView(),
      spaceBetween: this.options.spaceBetween,
      speed: this.options.speed,
      slidesOffsetBefore: this.calculateSlidesOffsetBefore(containerLg),
      slidesOffsetAfter: 24,
      breakpoints: {
        0: {
          slidesPerView: 1.2,
          slidesOffsetAfter: 24,
          spaceBetween: 16,
        },
        768: {
          slidesPerView: 2.3,
          slidesOffsetAfter: 36,
          spaceBetween: this.options.spaceBetween,
        },
        1024: {
          slidesPerView: 3.2,
          slidesOffsetAfter: 36,
          spaceBetween: this.options.spaceBetween,
        },
      },
      on: {
        init: () => this.updateSlidesPerView(),
        resize: () => this.updateSlidesPerView(),
      },
      navigation: {
        nextEl: '.product-lumen-user-review__navigation .swiper-button-next',
        prevEl: '.product-lumen-user-review__navigation .swiper-button-prev',
      },
    });

    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (this.swiper) {
          this.swiper.params.slidesOffsetBefore = this.calculateSlidesOffsetBefore(containerLg);
          this.swiper.update();
        }
      }, 100);
    });

    this.resizeObserver = new ResizeObserver(() => this.updateSlidesPerView());
    this.resizeObserver.observe(this.section);
    this.initReadMoreButtons();
  }

  /**
   * スライドの左オフセットを計算
   * @param {HTMLElement} containerLg - data-container="lg"要素
   * @returns {number} 左オフセット値
   * @private
   */
  calculateSlidesOffsetBefore(containerLg) {
    if (containerLg) {
      const containerRect = containerLg.getBoundingClientRect();
      const computedStyle = window.getComputedStyle(containerLg);
      const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;

      return containerRect.left + paddingLeft;
    }
    return 0;
  }

  /**
   * 表示スライド数を計算
   * @returns {number}
   * @private
   */
  calculateSlidesPerView() {
    const windowWidth = window.innerWidth;
    const containerWidth = this.section.offsetWidth;
    const { spaceBetween, breakpoints } = this.options;

    let slideWidth;
    if (windowWidth < 768) {
      slideWidth = breakpoints[0].slideWidth;
    } else if (windowWidth < 1200) {
      slideWidth = breakpoints[768].slideWidth;
    } else {
      slideWidth = breakpoints[1200].slideWidth;
    }

    return Math.floor((containerWidth / (slideWidth + spaceBetween)) * 100) / 100;
  }

  /**
   * 表示スライド数を更新
   * @private
   */
  updateSlidesPerView() {
    if (!this.swiper?.params) return;
    const windowWidth = window.innerWidth;
    if (windowWidth < 768) {
      this.swiper.params.slidesPerView = 1.2;
    } else if (windowWidth < 1024) {
      this.swiper.params.slidesPerView = 2.3;
    } else {
      this.swiper.params.slidesPerView = 3.2;
    }
    this.swiper.update();
  }

  /**
   * Read Moreボタンを初期化
   * @private
   */
  initReadMoreButtons() {
    const toggleButtons = this.section.querySelectorAll('.product-lumen-user-review__toggle');

    toggleButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const reviewContent = button.closest('.product-lumen-user-review__content');
        const isExpanded = reviewContent.classList.contains(
          'product-lumen-user-review__content--expanded',
        );
        reviewContent.classList.toggle('product-lumen-user-review__content--expanded', !isExpanded);
        button.setAttribute('aria-expanded', String(!isExpanded));
      });

      button.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          const reviewContent = button.closest('.product-lumen-user-review__content');
          reviewContent.classList.remove('product-lumen-user-review__content--expanded');
          button.setAttribute('aria-expanded', 'false');
        }
      });
    });
  }

  /**
   * 破棄
   */
  destroy() {
    this.resizeObserver?.disconnect();
    this.swiper?.destroy(true, true);
    this.resizeObserver = null;
    this.swiper = null;
  }
}

/**
 * 詳細説明比較コンポーネント
 * @class
 */
class DetailExplanationComparison {
  /**
   * @param {HTMLElement} sectionElement - セクション要素
   */
  constructor(sectionElement) {
    this.section = sectionElement;
    this.cards = [];
    this.init();
  }

  /**
   * 初期化
   * @private
   */
  init() {
    if (!this.section) return;
    const cardElements = this.section.querySelectorAll('.product-lumen-detail-explanation__card');

    cardElements.forEach((card) => {
      let isExpanded = false;

      card.addEventListener('click', () => {
        isExpanded = !isExpanded;
        card.classList.toggle('product-lumen-detail-explanation__card--expanded', isExpanded);
        card.setAttribute('aria-expanded', String(isExpanded));
      });

      card.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && isExpanded) {
          isExpanded = false;
          card.classList.remove('product-lumen-detail-explanation__card--expanded');
          card.setAttribute('aria-expanded', 'false');
        }
      });

      this.cards.push({ card, isExpanded });
    });
  }

  /**
   * 破棄
   */
  destroy() {
    this.cards = [];
  }
}

/**
 * カートウィジェットコンポーネント
 * @class
 */
class CartWidget {
  /**
   * @param {HTMLElement} sectionElement - セクション要素
   */
  constructor(sectionElement) {
    this.section = sectionElement;
    this.deviceSlider = null;
    this.videos = [];
    this.modal = null;
    this.init();
  }

  /**
   * 初期化
   * @private
   */
  init() {
    if (!this.section) return;
    this.selectedVariantId = null;
    this.initPlanCards();
    this.initDeviceSlider();
    this.initModal();
    this.initCoupon();
    this.initModalAccordion();
    this.initProductDetailAccordions();
    this.initializeFromCart();
  }

  /**
   * プランカードを初期化
   * @private
   */
  initPlanCards() {
    const planCards = this.section.querySelectorAll('.product-lumen-cart-widget__plan-card');
    planCards.forEach((card) => {
      card.addEventListener('click', () => this.handlePlanCardClick(card));
    });
  }

  /**
   * プランカードのクリック処理
   * @private
   * @param {HTMLElement} card - クリックされたカード要素
   */
  async handlePlanCardClick(card) {
    const variantId = card.dataset.variantId;
    if (!variantId) return;

    if (this.selectedVariantId === variantId) return;

    if (this.isLoading) return;

    const allCards = this.section.querySelectorAll('.product-lumen-cart-widget__plan-card');

    this.setLoadingState(true, card, allCards);

    try {
      await fetch('/cart/clear.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: '' }),
      });

      this.clearAppliedCoupons();
      this.clearCouponError();

      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: parseInt(variantId, 10), quantity: 1 }],
        }),
      });

      if (!addResponse.ok) {
        throw new Error('カートへの追加に失敗しました');
      }

      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      this.updateCheckoutPrice(cart.total_price);
      this.updateCheckoutButton(cart.item_count > 0);
      this.enableCouponInputs();

      allCards.forEach((c) => c.setAttribute('aria-selected', 'false'));

      card.setAttribute('aria-selected', 'true');
      this.selectedVariantId = variantId;

    } catch (error) {
      console.error('[CartWidget]:', error);
      this.showGlobalError('エラーが発生しました。しばらく時間をおいてから再度お試しください。');
    } finally {
      this.setLoadingState(false, card, allCards);
    }
  }

  /**
   * ローディング状態を設定
   * @private
   * @param {boolean} isLoading - ローディング中かどうか
   * @param {HTMLElement} activeCard - アクティブなカード
   * @param {NodeList} allCards - 全てのカード
   */
  setLoadingState(isLoading, activeCard, allCards) {
    this.isLoading = isLoading;

    const couponInput = this.section.querySelector('.product-lumen-cart-widget__coupon-input');
    const couponButton = this.section.querySelector('.product-lumen-cart-widget__coupon-button');
    const checkoutButton = this.section.querySelector('.product-lumen-cart-widget__checkout-button');

    if (isLoading) {
      activeCard.classList.add('is-loading');
      allCards.forEach((c) => {
        if (c !== activeCard) c.setAttribute('disabled', '');
      });

      if (couponInput) couponInput.setAttribute('disabled', '');
      if (couponButton) couponButton.setAttribute('disabled', '');
      if (checkoutButton) checkoutButton.setAttribute('aria-disabled', 'true');
    } else {
      activeCard.classList.remove('is-loading');
      allCards.forEach((c) => {
        if (c.dataset.available === 'true') {
          c.removeAttribute('disabled');
        }
      });

      if (couponInput) couponInput.removeAttribute('disabled');
      if (couponButton) couponButton.removeAttribute('disabled');
      if (checkoutButton) checkoutButton.removeAttribute('aria-disabled');
    }
  }

  /**
   * チェックアウト価格を更新
   * @private
   * @param {number} price - 価格（銭単位）
   */
  updateCheckoutPrice(price) {
    const priceElement = this.section.querySelector('#checkout-price');
    if (!priceElement) return;

    const formattedPrice = Math.floor(price / 100).toLocaleString('ja-JP');
    priceElement.textContent = formattedPrice;
  }

  /**
   * チェックアウトボタンの状態を更新
   * @private
   * @param {boolean} enabled - 有効にするかどうか
   */
  updateCheckoutButton(enabled) {
    const checkoutButton = this.section.querySelector('.product-lumen-cart-widget__checkout-button');
    if (!checkoutButton) return;

    if (enabled) {
      checkoutButton.removeAttribute('aria-disabled');
    } else {
      checkoutButton.setAttribute('aria-disabled', 'true');
    }
  }

  /**
   * カートの状態からUIを初期化
   * @private
   * @returns {Promise<void>}
   */
  async initializeFromCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      this.enablePlanCards();

      const hasSoldOutItem = cart.items.some((item) => {
        const card = this.section.querySelector(
          `.product-lumen-cart-widget__plan-card[data-variant-id="${item.variant_id}"]`
        );
        return card && card.dataset.available === 'false';
      });

      const isInvalidCart =
        cart.items.length > 1 ||
        (cart.items.length === 1 && cart.items[0].quantity > 1) ||
        hasSoldOutItem;

      if (isInvalidCart) {
        await fetch('/cart/clear.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        });
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: '' }),
        });
        return;
      }

      if (cart.items.length === 1) {
        const item = cart.items[0];
        const variantId = String(item.variant_id);

        const allCards = this.section.querySelectorAll('.product-lumen-cart-widget__plan-card');
        allCards.forEach((card) => {
          if (card.dataset.variantId === variantId) {
            card.setAttribute('aria-selected', 'true');
            this.selectedVariantId = variantId;
          } else {
            card.setAttribute('aria-selected', 'false');
          }
        });

        this.updateCheckoutPrice(cart.total_price);
        this.updateCheckoutButton(true);
        this.enableCouponInputs();
        this.syncCouponsFromCart(cart);
      }

    } catch (error) {
      console.error('[CartWidget]:', error);
      this.showGlobalError('エラーが発生しました。しばらく時間をおいてから再度お試しください。');
    }
  }

  /**
   * グローバルエラーを表示
   * @private
   * @param {string} message - エラーメッセージ
   * @param {number} duration - 表示時間（ミリ秒）
   */
  showGlobalError(message, duration = 5000) {
    const errorElement = this.section.querySelector('.product-lumen-cart-widget__global-error');
    if (!errorElement) return;

    errorElement.textContent = message;
    errorElement.classList.add('is-visible');

    setTimeout(() => {
      errorElement.classList.remove('is-visible');
      setTimeout(() => {
        errorElement.textContent = '';
      }, 300);
    }, duration);
  }

  /**
   * プランカードを有効化（カートクリア後）
   * 販売不可（data-available="false"）のカードは有効化しない
   * @private
   */
  enablePlanCards() {
    const planCards = this.section.querySelectorAll('.product-lumen-cart-widget__plan-card');
    planCards.forEach((card) => {
      if (card.dataset.available === 'true') {
        card.removeAttribute('disabled');
      }
    });
  }

  /**
   * クーポン入力を有効化（商品選択後）
   * @private
   */
  enableCouponInputs() {
    const couponInput = this.section.querySelector('.product-lumen-cart-widget__coupon-input');
    if (couponInput) couponInput.removeAttribute('disabled');

    const couponButton = this.section.querySelector('.product-lumen-cart-widget__coupon-button');
    if (couponButton) couponButton.removeAttribute('disabled');
  }

  /**
   * デバイススライダーを初期化
   * @private
   */
  initDeviceSlider() {
    const sliderContainer = this.section.querySelector('.product-lumen-cart-widget__media-slider');
    if (!sliderContainer) return;

    this.deviceSlider = new Swiper(sliderContainer, {
      loop: false,
      autoplay: false,
      slidesPerView: 1,
      spaceBetween: 0,
      breakpoints: {
        0: {
          slidesPerView: 1.2,
          spaceBetween: 16,
          slidesOffsetBefore: 24,
          slidesOffsetAfter: 24,
        },
        768: {
          slidesPerView: 1.5,
          spaceBetween: 24,
          slidesOffsetBefore: 36,
          slidesOffsetAfter: 36,
        },
        1024: {
          slidesPerView: 1,
          spaceBetween: 0,
          slidesOffsetBefore: 0,
          slidesOffsetAfter: 0,
        },
      },
      navigation: {
        nextEl: '.product-lumen-cart-widget__media-nav-button--next',
        prevEl: '.product-lumen-cart-widget__media-nav-button--prev',
      },
      pagination: {
        el: '.product-lumen-cart-widget__media-pagination',
        clickable: true,
        type: 'bullets',
      },
      on: {
        slideChangeTransitionEnd: () => this.handleSlideChange(),
      },
    });

    this.videos = [...sliderContainer.querySelectorAll('[data-video]')];
    requestAnimationFrame(() => this.handleSlideChange());
  }

  /**
   * スライド変更時の処理
   * @private
   */
  handleSlideChange() {
    const activeSlide = this.section.querySelector('.swiper-slide-active');
    if (!activeSlide) return;
    const activeVideo = activeSlide.querySelector('[data-video]');

    this.videos.forEach((video) => {});

    if (activeVideo) {
      this.safePlay(activeVideo);
    }
  }

  /**
   * 安全にビデオを再生
   * @param {HTMLVideoElement} video - ビデオ要素
   * @private
   */
  safePlay(video) {
    requestAnimationFrame(() => {
      const promise = video.play();
      if (promise?.then) {
        promise.catch(() => {
          requestAnimationFrame(() => video.play().catch(() => {}));
        });
      }
    });
  }

  /**
   * モーダルを初期化
   * @private
   */
  initModal() {
    const modalTrigger = this.section.querySelector('.product-lumen-cart-widget__modal-trigger');
    const modalOverlay = this.section.querySelector('.product-lumen-cart-widget__modal-overlay');
    const modalClose = this.section.querySelector('.product-lumen-cart-widget__modal-close');
    const modalDialog = this.section.querySelector('.product-lumen-cart-widget__modal-dialog');

    if (!modalTrigger || !modalOverlay || !modalDialog) return;

    this.modal = {
      trigger: modalTrigger,
      overlay: modalOverlay,
      close: modalClose,
      dialog: modalDialog,
    };
    let isOpen = false;

    const updateState = (open) => {
      isOpen = open;
      modalOverlay.classList.toggle('product-lumen-cart-widget__modal-overlay--open', isOpen);
      document.body.style.overflow = isOpen ? 'hidden' : '';
    };

    modalTrigger.addEventListener('click', () => updateState(true));
    modalClose?.addEventListener('click', () => updateState(false));
    modalOverlay.addEventListener('click', (e) => {
      if (e.target === modalOverlay) updateState(false);
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && isOpen) updateState(false);
    });
  }

  /**
   * クーポントグルを初期化
   * @private
   */
  /**
   * クーポン機能を初期化
   * @private
   */
  initCoupon() {
    this.couponInput = this.section.querySelector('.product-lumen-cart-widget__coupon-input');
    this.couponButton = this.section.querySelector('.product-lumen-cart-widget__coupon-button');
    this.appliedCouponList = this.section.querySelector('.product-lumen-cart-widget__applied-coupon');
    this.couponError = this.section.querySelector('.product-lumen-cart-widget__coupon-error');
    this.appliedCoupons = []; // 適用済みクーポンコードの配列

    if (!this.couponButton || !this.couponInput) return;

    this.couponButton.addEventListener('click', () => this.applyCoupon());
    this.couponInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.applyCoupon();
      }
    });

    this.couponInput.addEventListener('input', () => {
      this.clearCouponError();
    });
  }

  /**
   * cart.js からクーポンコードを取得
   * @private
   * @param {Object} cart
   * @returns {string[]}
   */
  getDiscountCodesFromCart(cart) {
    const discountCodes = (cart?.discount_codes || [])
      .filter((discount) => discount && discount.code && discount.applicable !== false)
      .map((discount) => String(discount.code).toUpperCase());

    const lineLevelCodes = (cart?.items || [])
      .flatMap((item) => item.line_level_discount_allocations || [])
      .map((allocation) => allocation?.discount_application)
      .filter((application) => application?.type === 'discount_code' && application.title && application.applicable !== false)
      .map((application) => String(application.title).toUpperCase());

    const cartLevelCodes = (cart?.cart_level_discount_applications || [])
      .filter((discount) => discount?.title && discount.applicable !== false)
      .map((discount) => String(discount.title).toUpperCase());

    return Array.from(new Set([...discountCodes, ...lineLevelCodes, ...cartLevelCodes]));
  }

  /**
   * cart.js からクーポン表示を同期
   * @private
   * @param {Object} cart
   */
  syncCouponsFromCart(cart) {
    const codes = this.getDiscountCodesFromCart(cart);
    this.appliedCoupons = codes;
    this.lastTotalDiscount = cart.total_discount;

    if (!this.appliedCouponList) return;
    this.appliedCouponList.innerHTML = '';
    codes.forEach((code) => this.addAppliedCoupon(code));
  }

  /**
   * クーポンを適用
   * @private
   */
  async applyCoupon() {
    const code = this.couponInput.value.trim().toUpperCase();
    if (!code) return;
    if (this.appliedCoupons.includes(code)) {
      this.showCouponError('このクーポンは既に適用されています');
      return;
    }
    this.clearCouponError();
    this.setCouponLoadingState(true);

    const newCoupons = [...this.appliedCoupons, code];

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: newCoupons.join(',') }),
      });

      if (!response.ok) {
        throw new Error('クーポンの適用に失敗しました');
      }

      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      const previousDiscount = this.lastTotalDiscount || 0;

      if (cart.total_discount > previousDiscount) {
        this.updateCheckoutPrice(cart.total_price);
        this.syncCouponsFromCart(cart);
      } else {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: this.appliedCoupons.join(',') }),
        });
        this.showCouponError('無効なクーポンコードです');
      }
    } catch (error) {
      console.error('[CartWidget]:', error);
      this.showCouponError('クーポンの適用に失敗しました');
    } finally {
      this.couponInput.value = '';
      this.setCouponLoadingState(false);
    }
  }

  /**
   * クーポン操作時のローディング状態を設定
   * @private
   * @param {boolean} isLoading - ローディング中かどうか
   */
  setCouponLoadingState(isLoading) {
    this.isLoading = isLoading;

    const planCards = this.section.querySelectorAll('.product-lumen-cart-widget__plan-card');
    const checkoutButton = this.section.querySelector('.product-lumen-cart-widget__checkout-button');

    if (isLoading) {
      this.couponButton.classList.add('is-loading');
      this.couponButton.disabled = true;
      this.couponInput.disabled = true;

      planCards.forEach((c) => c.setAttribute('disabled', ''));
      if (checkoutButton) checkoutButton.setAttribute('aria-disabled', 'true');
    } else {
      this.couponButton.classList.remove('is-loading');
      this.couponButton.disabled = false;
      this.couponInput.disabled = false;

      planCards.forEach((c) => {
        if (c.dataset.available === 'true') {
          c.removeAttribute('disabled');
        }
      });
      if (checkoutButton) checkoutButton.removeAttribute('aria-disabled');
    }
  }

  /**
   * 適用済みクーポンを表示
   * @private
   * @param {string} code - クーポンコード
   */
  addAppliedCoupon(code) {
    if (!this.appliedCouponList) return;

    const iconSpritesUrl = this.section.dataset.iconSpritesUrl || '';
    const li = document.createElement('li');
    li.className = 'product-lumen-cart-widget__applied-coupon-button';
    li.dataset.couponCode = code;
    li.innerHTML = `
      <span>${code}</span>
      <button class="product-lumen-cart-widget__applied-coupon-cancel-button" aria-label="クーポンを削除">
        <svg width="24" height="24" aria-hidden="true">
          <use href="${iconSpritesUrl}#icon-close"></use>
        </svg>
      </button>
    `;

    const cancelButton = li.querySelector('.product-lumen-cart-widget__applied-coupon-cancel-button');
    cancelButton.addEventListener('click', () => this.removeCoupon(li, code));

    this.appliedCouponList.appendChild(li);
  }

  /**
   * クーポンを削除
   * @private
   * @param {HTMLElement} couponElement - クーポン要素
   * @param {string} code - クーポンコード
   */
  async removeCoupon(couponElement, code) {
    this.clearCouponError();
    this.setCouponLoadingState(true);
    this.appliedCoupons = this.appliedCoupons.filter((c) => c !== code);

    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: this.appliedCoupons.join(',') }),
      });

      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      this.updateCheckoutPrice(cart.total_price);
      this.syncCouponsFromCart(cart);
    } catch (error) {
      console.error('[CartWidget]:', error);
      this.appliedCoupons.push(code);
      this.showCouponError('クーポンが削除できません。しばらく時間をおいてから再度お試しください。');
    } finally {
      this.setCouponLoadingState(false);
    }
  }

  /**
   * クーポンエラーを表示
   * @private
   * @param {string} message - エラーメッセージ
   */
  showCouponError(message) {
    if (!this.couponError) return;
    this.couponError.textContent = message;
  }

  /**
   * クーポンエラーをクリア
   * @private
   */
  clearCouponError() {
    if (!this.couponError) return;
    this.couponError.textContent = '';
  }

  /**
   * 適用済みクーポンをクリア
   * @private
   */
  clearAppliedCoupons() {
    this.appliedCoupons = [];
    this.lastTotalDiscount = 0;
    if (!this.appliedCouponList) return;
    this.appliedCouponList.innerHTML = '';
  }

  /**
   * 商品詳細アコーディオンを初期化
   * @private
   */
  initProductDetailAccordions() {
    const accordions = this.section.querySelectorAll(
      '.product-lumen-cart-widget__detail-accordion',
    );
    if (!accordions.length) return;

    let openAccordion = null;

    accordions.forEach((accordion) => {
      const trigger = accordion.querySelector('.product-lumen-cart-widget__detail-trigger');
      const content = accordion.querySelector('.product-lumen-cart-widget__detail-content');
      const icon = accordion.querySelector('.product-lumen-cart-widget__detail-icon');
      if (!trigger || !content) return;

      if (trigger.getAttribute('aria-expanded') === 'true') {
        content.classList.add('product-lumen-cart-widget__detail-content--open');
        content.style.maxHeight = content.scrollHeight + 'px';
        icon?.classList.add('product-lumen-cart-widget__detail-icon--open');
        openAccordion = accordion;
      }

      trigger.addEventListener('click', () => {
        const isExpanded = trigger.getAttribute('aria-expanded') === 'true';

        if (openAccordion && openAccordion !== accordion) {
          const openTrigger = openAccordion.querySelector(
            '.product-lumen-cart-widget__detail-trigger',
          );
          const openContent = openAccordion.querySelector(
            '.product-lumen-cart-widget__detail-content',
          );
          const openIcon = openAccordion.querySelector('.product-lumen-cart-widget__detail-icon');

          openContent.style.maxHeight = '0px';
          openContent.classList.remove('product-lumen-cart-widget__detail-content--open');
          openTrigger.setAttribute('aria-expanded', 'false');
          openIcon?.classList.remove('product-lumen-cart-widget__detail-icon--open');
        }

        if (isExpanded) {
          content.style.maxHeight = '0px';
          content.classList.remove('product-lumen-cart-widget__detail-content--open');
          trigger.setAttribute('aria-expanded', 'false');
          icon?.classList.remove('product-lumen-cart-widget__detail-icon--open');
          openAccordion = null;
        } else {
          content.classList.add('product-lumen-cart-widget__detail-content--open');
          content.style.maxHeight = content.scrollHeight + 'px';
          trigger.setAttribute('aria-expanded', 'true');
          icon?.classList.add('product-lumen-cart-widget__detail-icon--open');
          openAccordion = accordion;
        }
      });
    });
  }

  /**
   * モーダル内アコーディオンを初期化
   * @private
   */
  initModalAccordion() {
    const items = this.section.querySelectorAll('.product-lumen-cart-widget__modal-accordion-item');
    if (!items.length) return;

    items.forEach((item) => {
      const trigger = item.querySelector('.product-lumen-cart-widget__modal-accordion-trigger');
      const content = item.querySelector('.product-lumen-cart-widget__modal-accordion-content');
      if (!trigger || !content) return;

      trigger.addEventListener('click', () => {
        const isExpanded = trigger.getAttribute('aria-expanded') === 'true';

        items.forEach((otherItem) => {
          const otherTrigger = otherItem.querySelector(
            '.product-lumen-cart-widget__modal-accordion-trigger',
          );
          const otherContent = otherItem.querySelector(
            '.product-lumen-cart-widget__modal-accordion-content',
          );
          if (otherTrigger && otherContent) {
            otherTrigger.setAttribute('aria-expanded', 'false');
            otherItem.classList.remove('is-open');
            otherContent.style.maxHeight = '0';
          }
        });

        if (!isExpanded) {
          trigger.setAttribute('aria-expanded', 'true');
          item.classList.add('is-open');
          content.style.maxHeight = content.scrollHeight + 'px';
        }
      });
    });
  }

  /**
   * 破棄
   */
  destroy() {
    this.deviceSlider?.destroy(true, true);
    this.deviceSlider = null;
    this.videos = [];
  }
}

/**
 * 科学的証拠アニメーションコンポーネント
 * @class
 */
class ScientificProofAnimation {
  /**
   * @param {HTMLElement} container - コンテナ要素
   * @param {Object} options - オプション
   * @param {number} [options.itemDuration=1.5] - 各要素のアニメーション時間（秒）
   * @param {number} [options.gapDuration=1.5] - ループ間の余白（秒）
   */
  constructor(container, options = {}) {
    this.container = container;
    this.items = Array.from(container.querySelectorAll('em'));
    this.options = {
      itemDuration: options.itemDuration || 1.5,
      gapDuration: options.gapDuration || 1.5,
    };
    this.init();
  }

  /**
   * 初期化
   * @private
   */
  init() {
    if (this.items.length === 0) return;

    const totalDuration = this.items.length * this.options.itemDuration + this.options.gapDuration;

    this.items.forEach((item, index) => {
      const delay = index * this.options.itemDuration;
      item.style.animationDelay = `${delay}s`;
      item.style.animationDuration = `${totalDuration}s`;
    });

    this.createKeyframes(totalDuration);
  }

  /**
   * キーフレームを動的に生成
   * @param {number} totalDuration - 総アニメーション時間
   * @private
   */
  createKeyframes(totalDuration) {
    const styleId = 'product-lumen-scientific-proof-keyframes';
    let style = document.getElementById(styleId);

    if (!style) {
      style = document.createElement('style');
      style.id = styleId;
      document.head.appendChild(style);
    }

    const animDuration = this.options.itemDuration;
    const midPercent = ((animDuration * 0.5) / totalDuration) * 100;
    const endPercent = (animDuration / totalDuration) * 100;

    style.textContent = `
      @keyframes roei-wtf-loop {
        0% {
          transform: translateY(10px);
          background: linear-gradient(to right, rgb(60, 6, 156), rgb(75, 5, 200), rgb(132, 5, 230), rgb(170, 14, 248), rgb(222, 130, 255));
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        ${midPercent.toFixed(2)}% {
          background: linear-gradient(to right, rgb(60, 6, 156), rgb(75, 5, 200), rgb(132, 5, 230), rgb(170, 14, 248), rgb(222, 130, 255));
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        ${endPercent.toFixed(2)}% {
          transform: translateY(0);
          background: none;
          -webkit-text-fill-color: inherit;
          color: #000;
        }
        100% {
          transform: translateY(0);
          background: none;
          -webkit-text-fill-color: inherit;
          color: #000;
        }
      }
    `;
  }

  /**
   * 破棄
   */
  destroy() {
    const style = document.getElementById('product-lumen-scientific-proof-keyframes');
    style?.remove();
  }
}

/**
 * DOM読み込み完了時に各コンポーネントを初期化
 */
document.addEventListener('DOMContentLoaded', () => {
  const scientificProofContainer = document.querySelector(
    '[data-product-lumen-scientific-proof-animation]',
  );
  if (scientificProofContainer) {
    new ScientificProofAnimation(scientificProofContainer, {
      itemDuration: 2.5,
      gapDuration: 2.5,
    });
  }

  const productLumenHeroSection = document.querySelector('.product-lumen-hero-section');
  if (productLumenHeroSection) {
    const heroMediaSlider = productLumenHeroSection.querySelector(
      '.product-lumen-hero-section__media-slider',
    );
    if (heroMediaSlider) new ProductLumenHeroMediaSlider(heroMediaSlider);

    new ProductLumenHeroTrustpilotModal(productLumenHeroSection);

    const accordion = productLumenHeroSection.querySelector(
      '.product-lumen-hero-section__accordion',
    );
    if (accordion) new ProductLumenHeroAccordion(accordion);

    const heroMainContent = productLumenHeroSection.querySelector(
      '.product-lumen-hero-section__main-content',
    );
    if (heroMainContent && window.$store?.shopPageState) {
      new ProductLumenHeroVisibilityObserver(heroMainContent, (isVisible) => {
        window.$store.shopPageState.setStickyBannerVisible(!isVisible);
      });
    }
  }

  const habitsSliderSection = document.querySelector('.product-lumen-habits-slider');
  if (habitsSliderSection) {
    new HabitsSlider(habitsSliderSection, { speed: 1000 });
  }

  const hardwareSection = document.querySelector('.product-lumen-hardware-characteristics');
  if (hardwareSection) {
    new HardwareCharacteristics(hardwareSection);
  }

  const userExperienceSection = document.querySelector('.product-lumen-user-experience');
  if (userExperienceSection) {
    new UserExperienceSlider(userExperienceSection, { speed: 1000 });
  }

  const userReviewSection = document.querySelector('.product-lumen-user-review');
  if (userReviewSection) {
    new UserReviewSlider(userReviewSection, { speed: 1000 });
  }

  const detailExplanationSection = document.querySelector('.product-lumen-detail-explanation');
  if (detailExplanationSection) {
    new DetailExplanationComparison(detailExplanationSection);
  }

  const cartWidgetSection = document.querySelector('.product-lumen-cart-widget');
  if (cartWidgetSection) {
    new CartWidget(cartWidgetSection);
  }
});
